import * as React from "react";
import { addDays, addWeeks, format } from "date-fns";
import pt from "date-fns/locale/pt";

import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { DATE_DAY_FROM_MONTH } from "@/services/formatters";
import moneyFormatter from "@/filters/money.js";
import { cn } from "@/lib/utils";
import type { Purchase } from "../types";

const PURCHASE_DATE_FORMAT = "dd/MM/yy";

const EUROPE_DELIVERY_MARGIN_DAYS = 4;
const OTHER_DELIVERY_START_WEEKS = 2;
const OTHER_DELIVERY_END_WEEKS = 6;

const computeEstimate = (purchase: Purchase) => {
	const createdAt = new Date(purchase.createdAt);

	if (Number.isNaN(createdAt.getTime())) {
		return "-";
	}

	if (purchase.origin === "europe") {
		const end = addDays(createdAt, EUROPE_DELIVERY_MARGIN_DAYS);

		return `(${format(createdAt, DATE_DAY_FROM_MONTH, { locale: pt })} - ${format(
			end,
			DATE_DAY_FROM_MONTH,
			{ locale: pt },
		)})`;
	}

	const start = addWeeks(createdAt, OTHER_DELIVERY_START_WEEKS);
	const end = addWeeks(createdAt, OTHER_DELIVERY_END_WEEKS);

	return `(${format(start, DATE_DAY_FROM_MONTH, { locale: pt })} - ${format(
		end,
		DATE_DAY_FROM_MONTH,
		{ locale: pt },
	)})`;
};

const formatDate = (value: string) => {
	const date = new Date(value);

	if (Number.isNaN(date.getTime())) {
		return "-";
	}

	return format(date, PURCHASE_DATE_FORMAT, { locale: pt });
};

const formatOrigin = (origin: Purchase["origin"]) => {
	if (origin === "europe") {
		return "Europa";
	}

	if (origin === "other") {
		return "Fora da Europa";
	}

	return "-";
};

const formatPrice = (purchase: Purchase) => {
	const price = purchase.price ?? purchase.material?.price ?? 0;
	return moneyFormatter(Number(price) || 0);
};

const rowHighlightClasses = (state?: string) => {
	switch (state) {
		case "overtime":
			return "bg-red-100/70 text-red-900 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-50 dark:hover:bg-red-900/40";
		case "first_verification":
			return "bg-amber-100/70 text-amber-900 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-50 dark:hover:bg-amber-900/40";
		default:
			return "hover:bg-muted/40";
	}
};

type PurchasesTableProps = {
	purchases: Purchase[];
	isLoading?: boolean;
	onConfirm: (purchase: Purchase) => void;
	onEdit: (purchase: Purchase) => void;
	onCancel: (purchase: Purchase) => void;
};

export const PurchasesTable: React.FC<PurchasesTableProps> = ({
	purchases,
	isLoading = false,
	onConfirm,
	onEdit,
	onCancel,
}) => {
	const renderSkeleton = () => (
		<TableBody>
			{Array.from({ length: 5 }).map((_, index) => (
				<TableRow key={`purchase-skeleton-${index}`} className="animate-pulse">
					<TableCell>
						<div className="h-4 w-48 rounded bg-muted" />
					</TableCell>
					<TableCell className="text-center">
						<div className="mx-auto h-4 w-10 rounded bg-muted/70" />
					</TableCell>
					<TableCell className="text-center">
						<div className="mx-auto h-4 w-16 rounded bg-muted/60" />
					</TableCell>
					<TableCell className="text-center">
						<div className="mx-auto h-4 w-24 rounded bg-muted/50" />
					</TableCell>
					<TableCell className="text-center">
						<div className="mx-auto h-4 w-28 rounded bg-muted/40" />
					</TableCell>
					<TableCell className="text-center">
						<div className="mx-auto h-4 w-10 rounded bg-muted/50" />
					</TableCell>
					<TableCell className="text-center">
						<div className="mx-auto h-4 w-20 rounded bg-muted/40" />
					</TableCell>
					<TableCell className="text-right">
						<div className="ml-auto h-8 w-44 rounded bg-muted/30" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	const renderRows = () => (
		<TableBody>
			{purchases.map((purchase) => (
				<TableRow
					key={purchase.id}
					className={cn(
						"transition-colors",
						rowHighlightClasses(purchase.state),
					)}
				>
					<TableCell className="font-medium text-foreground">
						{(() => {
							const brandModel = [
								purchase.material?.brand,
								purchase.material?.model,
							]
								.filter(Boolean)
								.join(" ")
								.trim();

							const description = purchase.material?.description?.trim();

							if (brandModel && description) {
								return `${brandModel}, ${description}`;
							}

							return brandModel || description || "-";
						})()}
					</TableCell>
					<TableCell className="text-center">{purchase.quantity}</TableCell>
					<TableCell className="text-center">
						{formatOrigin(purchase.origin)}
					</TableCell>
					<TableCell className="text-center">
						{formatDate(purchase.createdAt)}
					</TableCell>
					<TableCell className="text-center">
						{computeEstimate(purchase)}
					</TableCell>
					<TableCell className="text-center">
						{purchase.link ? (
							<a
								href={purchase.link}
								target="_blank"
								rel="noreferrer"
								className="text-primary underline underline-offset-2"
							>
								Link
							</a>
						) : (
							<span className="text-muted-foreground">-</span>
						)}
					</TableCell>
					<TableCell className="text-center">{formatPrice(purchase)}</TableCell>
					<TableCell className="flex justify-end gap-2">
						<Button
							type="button"
							className="bg-emerald-600 text-white hover:bg-emerald-500"
							size="sm"
							onClick={() => onConfirm(purchase)}
						>
							Efetivar
						</Button>
						<Button
							type="button"
							variant="secondary"
							size="icon-sm"
							onClick={() => onEdit(purchase)}
							aria-label={`Editar compra ${purchase.id}`}
						>
							<i className="fa fa-pencil" aria-hidden />
						</Button>
						<Button
							type="button"
							variant="destructive"
							size="icon-sm"
							onClick={() => onCancel(purchase)}
							aria-label={`Cancelar compra ${purchase.id}`}
						>
							<i className="fa fa-trash" aria-hidden />
						</Button>
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	const renderEmpty = () => (
		<TableBody>
			<TableRow>
				<TableCell
					colSpan={8}
					className="py-10 text-center text-sm text-muted-foreground"
				>
					Não existem compras registadas.
				</TableCell>
			</TableRow>
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-md border">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Descrição</TableHead>
						<TableHead className="w-[70px] text-center">Qtn.</TableHead>
						<TableHead className="w-[100px] text-center">Origem</TableHead>
						<TableHead className="w-[160px] text-center">Compra</TableHead>
						<TableHead className="w-[200px] text-center">Previsão</TableHead>
						<TableHead className="w-[90px] text-center">Link</TableHead>
						<TableHead className="w-[120px] text-center">Preço</TableHead>
						<TableHead className="w-[220px] text-right" aria-hidden="true">
							Ações
						</TableHead>
					</TableRow>
				</TableHeader>

				{isLoading
					? renderSkeleton()
					: purchases.length > 0
						? renderRows()
						: renderEmpty()}
			</Table>
		</div>
	);
};
